// ============================================
// SettingsPage Component
// Application and store settings management
// ============================================

import { useEffect, useMemo, useState, useRef } from 'react';
import { PageHeader, PageContainer } from '../../components/shared';
import {
  useSettings,
  useUpdateGeneralSettings,
  useUpdateNotificationSettings,
  useUpdateSecuritySettings,
} from '../../hooks';
import { useSettingsStore } from '../../stores';
import { useI18n } from '../../i18n';
import { SystemParametersTab } from './components/SystemParametersTab';

// ============================================
// Types
// ============================================

type SettingsTab = 'general' | 'notifications' | 'security' | 'integrations' | 'parameters';

interface GeneralSettings {
  storeName: string;
  storeEmail: string;
  timezone: string;
  currency: string;
  language: string;
}

interface NotificationSettings {
  emailNotifications: boolean;
  lowStockAlerts: boolean;
  orderNotifications: boolean;
  dailyReports: boolean;
  weeklyDigest: boolean;
}

interface SecuritySettings {
  twoFactorEnabled: boolean;
  sessionTimeout: number;
  passwordExpiry: number;
  ipWhitelist: string;
}

interface IntegrationItem {
  name: string;
  description: string;
  status: 'connected' | 'disconnected' | 'pending';
}

// ============================================
// Tab Navigation
// ============================================

function TabNav({ active, onChange }: { active: SettingsTab; onChange: (tab: SettingsTab) => void }) {
  const { t } = useI18n();
  const tabs: { id: SettingsTab; label: string; icon: string }[] = [
    { id: 'general', label: t('settingsGeneral'), icon: '⚙️' },
    { id: 'notifications', label: t('settingsNotifications'), icon: '🔔' },
    { id: 'security', label: t('settingsSecurity'), icon: '🔒' },
    { id: 'integrations', label: t('settingsIntegrations'), icon: '🔗' },
    // Tham số vận hành (mục #234) — đứng CUỐI có chủ đích: bốn tab trước là
    // form an toàn, tab này sửa thẳng khoá hệ thống.
    { id: 'parameters', label: t('settingsParameters'), icon: '🗝️' },
  ];

  return (
    <div className="border-b border-slate-200">
      <nav className="flex gap-4 overflow-x-auto sm:gap-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onChange(tab.id)}
            className={`flex shrink-0 items-center gap-2 whitespace-nowrap py-4 text-sm font-medium border-b-2 transition-colors ${
              active === tab.id
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <span>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </nav>
    </div>
  );
}

// ============================================
// Settings Section Component
// ============================================

function SettingsSection({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl bg-white p-6 shadow-sm border border-slate-100">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        <p className="mt-1 text-sm text-slate-500">{description}</p>
      </div>
      {children}
    </div>
  );
}

// ============================================
// Form Components
// ============================================

function FormField({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

function TextInput({
  value,
  onChange,
  type = 'text',
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none disabled:bg-slate-50 disabled:text-slate-400"
    />
  );
}

function SelectInput({
  value,
  onChange,
  options,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none disabled:bg-slate-50"
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

function ToggleSwitch({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? 'bg-primary-600' : 'bg-slate-200'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

function ToggleField({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
      <div>
        <p className="text-sm font-medium text-slate-700">{label}</p>
        <p className="text-xs text-slate-500">{description}</p>
      </div>
      <ToggleSwitch checked={checked} onChange={onChange} disabled={disabled} />
    </div>
  );
}

// ============================================
// General Settings Tab
// ============================================

function GeneralSettingsTab({
  settings,
  onChange,
  onLanguageChange,
}: {
  settings: GeneralSettings;
  onChange: (settings: GeneralSettings) => void;
  onLanguageChange: (language: 'pl' | 'en' | 'uk') => void;
}) {
  const { t } = useI18n();
  return (
    <div className="space-y-6">
      <SettingsSection
        title={t('settingsStoreInfoTitle')}
        description={t('settingsStoreInfoDesc')}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label={t('settingsStoreName')}>
            <TextInput
              value={settings.storeName}
              onChange={(v) => onChange({ ...settings, storeName: v })}
            />
          </FormField>
          <FormField label={t('settingsStoreEmail')}>
            <TextInput
              value={settings.storeEmail}
              onChange={(v) => onChange({ ...settings, storeEmail: v })}
              type="email"
            />
          </FormField>
        </div>
      </SettingsSection>

      <SettingsSection
        title={t('settingsRegionalTitle')}
        description={t('settingsRegionalDesc')}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormField label={t('settingsTimezone')}>
            <SelectInput
              value={settings.timezone}
              onChange={(v) => onChange({ ...settings, timezone: v })}
              options={[
                { value: 'Asia/Ho_Chi_Minh', label: t('settingsTimezoneVietnam') },
                { value: 'Asia/Bangkok', label: t('settingsTimezoneThailand') },
                { value: 'Asia/Singapore', label: t('settingsTimezoneSingapore') },
                { value: 'Asia/Tokyo', label: t('settingsTimezoneJapan') },
              ]}
            />
          </FormField>
          <FormField label={t('settingsCurrency')}>
            <SelectInput
              value={settings.currency}
              onChange={(v) => onChange({ ...settings, currency: v })}
              options={[
                { value: 'VND', label: t('settingsCurrencyVND') },
                { value: 'USD', label: t('settingsCurrencyUSD') },
                { value: 'EUR', label: t('settingsCurrencyEUR') },
              ]}
            />
          </FormField>
          <FormField label={t('settingsLanguage')}>
            <SelectInput
              value={settings.language}
              onChange={(v) => {
                if (v === 'pl' || v === 'en' || v === 'uk') {
                  onLanguageChange(v);
                }
                onChange({ ...settings, language: v });
              }}
              options={[
                { value: 'en', label: t('languageEnglish') },
                { value: 'pl', label: t('languagePolish') },
                { value: 'uk', label: t('languageUkrainian') },
              ]}
            />
          </FormField>
        </div>
      </SettingsSection>
    </div>
  );
}

// ============================================
// Notifications Settings Tab
// ============================================

function NotificationsSettingsTab({
  settings,
  onChange,
}: {
  settings: NotificationSettings;
  onChange: (settings: NotificationSettings) => void;
}) {
  const { t } = useI18n();
  return (
    <div className="space-y-6">
      <SettingsSection
        title={t('settingsEmailNotificationsTitle')}
        description={t('settingsEmailNotificationsDesc')}
      >
        <ToggleField
          label={t('settingsEmailNotificationsLabel')}
          description={t('settingsEmailNotificationsHint')}
          checked={settings.emailNotifications}
          onChange={(v) => onChange({ ...settings, emailNotifications: v })}
        />
        <ToggleField
          label={t('settingsLowStockAlertsLabel')}
          description={t('settingsLowStockAlertsHint')}
          checked={settings.lowStockAlerts}
          onChange={(v) => onChange({ ...settings, lowStockAlerts: v })}
        />
        <ToggleField
          label={t('settingsOrderNotificationsLabel')}
          description={t('settingsOrderNotificationsHint')}
          checked={settings.orderNotifications}
          onChange={(v) => onChange({ ...settings, orderNotifications: v })}
        />
      </SettingsSection>

      <SettingsSection
        title={t('settingsReportsTitle')}
        description={t('settingsReportsDesc')}
      >
        <ToggleField
          label={t('settingsDailyReportsLabel')}
          description={t('settingsDailyReportsHint')}
          checked={settings.dailyReports}
          onChange={(v) => onChange({ ...settings, dailyReports: v })}
        />
        <ToggleField
          label={t('settingsWeeklyDigestLabel')}
          description={t('settingsWeeklyDigestHint')}
          checked={settings.weeklyDigest}
          onChange={(v) => onChange({ ...settings, weeklyDigest: v })}
        />
      </SettingsSection>
    </div>
  );
}

// ============================================
// Security Settings Tab
// ============================================

function SecuritySettingsTab({
  settings,
  onChange,
}: {
  settings: SecuritySettings;
  onChange: (settings: SecuritySettings) => void;
}) {
  const { t } = useI18n();
  return (
    <div className="space-y-6">
      <SettingsSection
        title={t('settingsAuthenticationTitle')}
        description={t('settingsAuthenticationDesc')}
      >
        {/* 2FA giờ là mã EMAIL bắt buộc ở /auth/admin/login/ (chốt 08/09/2026),
            không còn gì để bật/tắt ở đây — panel TOTP cũ đã gỡ cùng với
            /auth/2fa/* phía BE. Miễn 2FA cho một tài khoản là việc của
            AI_ADMIN qua /users/{id}/disable-2fa/. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <FormField
            label={t('settingsSessionTimeoutLabel')}
            hint={t('settingsSessionTimeoutHint')}
          >
            <TextInput
              value={(settings.sessionTimeout ?? 30).toString()}
              onChange={(v) => onChange({ ...settings, sessionTimeout: parseInt(v) || 30 })}
              type="number"
            />
          </FormField>
          <FormField
            label={t('settingsPasswordExpiryLabel')}
            hint={t('settingsPasswordExpiryHint')}
          >
            <TextInput
              value={(settings.passwordExpiry ?? 0).toString()}
              onChange={(v) => onChange({ ...settings, passwordExpiry: parseInt(v) || 0 })}
              type="number"
            />
          </FormField>
        </div>
      </SettingsSection>

      <SettingsSection
        title={t('settingsAccessControlTitle')}
        description={t('settingsAccessControlDesc')}
      >
        <FormField
          label={t('settingsIpWhitelistLabel')}
          hint={t('settingsIpWhitelistHint')}
        >
          <TextInput
            value={settings.ipWhitelist}
            onChange={(v) => onChange({ ...settings, ipWhitelist: v })}
            placeholder="192.168.1.100, 10.0.0.1"
          />
        </FormField>
      </SettingsSection>
    </div>
  );
}

// ============================================
// Integrations Settings Tab
// ============================================

function IntegrationsSettingsTab({ integrations }: { integrations: IntegrationItem[] }) {
  const { t } = useI18n();

  const statusBadge = (status: string) => {
    const styles = {
      connected: 'bg-green-100 text-green-700',
      disconnected: 'bg-slate-100 text-slate-600',
      pending: 'bg-amber-100 text-amber-700',
    }[status] || 'bg-slate-100 text-slate-600';

    const labels = {
      connected: t('settingsStatusConnected'),
      disconnected: t('settingsStatusDisconnected'),
      pending: t('settingsStatusPending'),
    } as const;

    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${styles}`}>
        {labels[status as keyof typeof labels] || status}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <SettingsSection
        title={t('settingsConnectedServicesTitle')}
        description={t('settingsConnectedServicesDesc')}
      >
        <div className="divide-y divide-slate-100">
          {integrations.map((integration) => (
            <div
              key={integration.name}
              className="flex items-center justify-between py-4 first:pt-0 last:pb-0"
            >
              <div className="flex items-center gap-4">
                <span className="text-2xl">🔗</span>
                <div>
                  <p className="font-medium text-slate-900">{integration.name}</p>
                  <p className="text-sm text-slate-500">{integration.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {statusBadge(integration.status)}
                <button className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                  {t('settingsConfigure')}
                </button>
              </div>
            </div>
          ))}
        </div>
      </SettingsSection>

      <SettingsSection
        title={t('settingsApiKeysTitle')}
        description={t('settingsApiKeysDesc')}
      >
        <div className="rounded-lg bg-slate-50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="font-medium text-slate-900">{t('settingsProductionApiKey')}</p>
              <p className="truncate text-sm text-slate-500 font-mono">sk_live_****************************</p>
            </div>
            <button className="shrink-0 text-sm text-primary-600 hover:text-primary-700 font-medium">
              {t('settingsRegenerate')}
            </button>
          </div>
        </div>
      </SettingsSection>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('general');
  const [isSaving, setIsSaving] = useState(false);

  // API data
  const { general, notifications, security, integrations, isLoading, error } = useSettings();
  const updateGeneralMutation = useUpdateGeneralSettings();
  const updateNotificationsMutation = useUpdateNotificationSettings();
  const updateSecurityMutation = useUpdateSecuritySettings();
  const { t, setLanguage } = useI18n();

  // Zustand Store for holding unsaved temporary changes across page navigation
  const {
    localGeneral,
    localNotifications,
    localSecurity,
    setLocalGeneral,
    setLocalNotifications,
    setLocalSecurity,
    clearLocalState,
  } = useSettingsStore();

  // Local editable state initialization with store fallback
  const [generalSettings, setGeneralSettings] = useState<GeneralSettings>(
    localGeneral || {
      storeName: '',
      storeEmail: '',
      timezone: 'Asia/Ho_Chi_Minh',
      currency: 'VND',
      language: 'vi',
    }
  );

  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>(
    localNotifications || {
      emailNotifications: false,
      lowStockAlerts: false,
      orderNotifications: false,
      dailyReports: false,
      weeklyDigest: false,
    }
  );

  const [securitySettings, setSecuritySettings] = useState<SecuritySettings>(
    localSecurity || {
      twoFactorEnabled: false,
      sessionTimeout: 30,
      passwordExpiry: 0,
      ipWhitelist: '',
    }
  );

  // Sync general changes to store
  const handleGeneralChange = (settings: GeneralSettings) => {
    setGeneralSettings(settings);
    setLocalGeneral(settings);
  };

  // Sync notifications changes to store
  const handleNotificationsChange = (settings: NotificationSettings) => {
    setNotificationSettings(settings);
    setLocalNotifications(settings);
  };

  // Sync security changes to store
  const handleSecurityChange = (settings: SecuritySettings) => {
    setSecuritySettings(settings);
    setLocalSecurity(settings);
  };

  const integrationItems: IntegrationItem[] = useMemo(() => {
    const list = Array.isArray(integrations)
      ? integrations
      : Array.isArray((integrations as any)?.data)
      ? (integrations as any).data
      : [];
    return list.map((item: any) => ({
      name: item.name || '',
      description: item.description || '',
      status: item.status || 'disconnected',
    }));
  }, [integrations]);

  const isInitializedRef = useRef({ general: false, notifications: false, security: false });

  // If there are unsaved local changes in the store, prevent API data from overwriting them
  useEffect(() => {
    if (localGeneral) {
      isInitializedRef.current.general = true;
    }
    if (localNotifications) {
      isInitializedRef.current.notifications = true;
    }
    if (localSecurity) {
      isInitializedRef.current.security = true;
    }
  }, [localGeneral, localNotifications, localSecurity]);

  // Reset initialization flag when mutation succeeds to allow loading new saved data from cache
  useEffect(() => {
    if (updateGeneralMutation.isSuccess) {
      isInitializedRef.current.general = false;
    }
  }, [updateGeneralMutation.isSuccess]);

  useEffect(() => {
    if (updateNotificationsMutation.isSuccess) {
      isInitializedRef.current.notifications = false;
    }
  }, [updateNotificationsMutation.isSuccess]);

  useEffect(() => {
    if (updateSecurityMutation.isSuccess) {
      isInitializedRef.current.security = false;
    }
  }, [updateSecurityMutation.isSuccess]);

  useEffect(() => {
    if (general && typeof general === 'object' && !isInitializedRef.current.general) {
      const raw = (general as any).data ?? general;
      if (!('detail' in raw)) {
        const initialized = {
          storeName: raw.store_name || '',
          storeEmail: raw.store_email || '',
          timezone: raw.timezone || 'Asia/Ho_Chi_Minh',
          currency: raw.currency || 'VND',
          language: raw.language || 'vi',
        };
        setGeneralSettings(initialized);
        isInitializedRef.current.general = true;
      }
    }
  }, [general]);

  useEffect(() => {
    if (general && typeof general === 'object') {
      const raw = (general as any).data ?? general;
      if (!('detail' in raw)) {
        if ((raw.language === 'pl' || raw.language === 'en' || raw.language === 'uk') && !localGeneral) {
          setLanguage(raw.language);
        }
      }
    }
  }, [general, setLanguage, localGeneral]);

  useEffect(() => {
    if (notifications && typeof notifications === 'object' && !isInitializedRef.current.notifications) {
      const raw = (notifications as any).data ?? notifications;
      if (!('detail' in raw)) {
        const initialized = {
          emailNotifications: !!raw.email_notifications,
          lowStockAlerts: !!raw.low_stock_alerts,
          orderNotifications: !!raw.order_notifications,
          dailyReports: !!raw.daily_reports,
          weeklyDigest: !!raw.weekly_digest,
        };
        setNotificationSettings(initialized);
        isInitializedRef.current.notifications = true;
      }
    }
  }, [notifications]);

  useEffect(() => {
    if (security && typeof security === 'object' && !isInitializedRef.current.security) {
      const raw = (security as any).data ?? security;
      if (!('detail' in raw)) {
        const initialized = {
          twoFactorEnabled: !!raw.two_factor_enabled,
          sessionTimeout: raw.session_timeout ?? 30,
          passwordExpiry: raw.password_expiry ?? 0,
          ipWhitelist: raw.ip_whitelist || '',
        };
        setSecuritySettings(initialized);
        isInitializedRef.current.security = true;
      }
    }
  }, [security]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const promises = [];
      if (localGeneral) {
        promises.push(
          updateGeneralMutation.mutateAsync({
            store_name: generalSettings.storeName,
            store_email: generalSettings.storeEmail,
            timezone: generalSettings.timezone,
            currency: generalSettings.currency,
            language: generalSettings.language,
          })
        );
      }
      if (localNotifications) {
        promises.push(
          updateNotificationsMutation.mutateAsync({
            email_notifications: notificationSettings.emailNotifications,
            low_stock_alerts: notificationSettings.lowStockAlerts,
            order_notifications: notificationSettings.orderNotifications,
            daily_reports: notificationSettings.dailyReports,
            weekly_digest: notificationSettings.weeklyDigest,
          })
        );
      }
      if (localSecurity) {
        promises.push(
          updateSecurityMutation.mutateAsync({
            two_factor_enabled: securitySettings.twoFactorEnabled,
            session_timeout: securitySettings.sessionTimeout,
            password_expiry: securitySettings.passwordExpiry,
            ip_whitelist: securitySettings.ipWhitelist,
          })
        );
      }

      if (promises.length > 0) {
        await Promise.all(promises);
      }
      // Clear local changes store on success
      clearLocalState();
    } finally {
      setIsSaving(false);
    }
  };

  try {
    return (
      <PageContainer>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <PageHeader
            title={t('settingsTitle')}
            subtitle={t('settingsSubtitle')}
          />
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="shrink-0 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? t('settingsSaving') : t('settingsSaveChanges')}
          </button>
        </div>

        {error && (
          <div className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">
            <p className="font-medium">{t('settingsLoadFailedTitle')}</p>
            <p className="mt-1 text-red-600">
              {error instanceof Error ? error.message : t('settingsLoadFailedBody')}
            </p>
          </div>
        )}

        {isLoading && (
          <div className="mt-6 rounded-xl bg-white shadow-sm border border-slate-100 p-8">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="mt-6">
          <TabNav active={activeTab} onChange={setActiveTab} />
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === 'general' && (
            <GeneralSettingsTab
              settings={generalSettings}
              onChange={handleGeneralChange}
              onLanguageChange={setLanguage}
            />
          )}
          {activeTab === 'notifications' && (
            <NotificationsSettingsTab settings={notificationSettings} onChange={handleNotificationsChange} />
          )}
          {activeTab === 'security' && (
            <SecuritySettingsTab settings={securitySettings} onChange={handleSecurityChange} />
          )}
          {activeTab === 'integrations' && <IntegrationsSettingsTab integrations={integrationItems} />}
          {activeTab === 'parameters' && <SystemParametersTab />}
        </div>
      </PageContainer>
    );
  } catch (renderError) {
    return (
      <PageContainer>
        <div className="mt-6 rounded-xl border border-red-200 bg-red-50/50 p-6 text-slate-800">
          <h3 className="text-lg font-semibold text-red-800">Unable to load Settings Interface</h3>
          <p className="mt-2 text-sm text-slate-600">
            The page failed to render due to missing or invalid configuration data from the server.
          </p>
          {renderError instanceof Error && (
            <pre className="mt-4 overflow-x-auto rounded-lg bg-slate-900 p-4 text-xs font-mono text-slate-200">
              {renderError.message}
            </pre>
          )}
        </div>
      </PageContainer>
    );
  }
}
